import type { Screen } from '~/services/layout/index.ts';
import { getPluginConfig } from '~/utils/get-plugin-configuration.ts';
import formScreenManifest from './config/manifest.ts';

export const DEFAULT_FORM_VALIDATION_MESSAGES = {
  required: 'This field is required',
  invalidDate: 'Please enter a valid date',
  invalidNumber: 'Please enter a valid number',
  minLength: 'Minimum length is {minLength}',
  maxLength: 'Maximum length is {maxLength}',
} as const;

export type FormValidationMessages = {
  required: string;
  invalidDate: string;
  invalidNumber: string;
  minLength: string;
  maxLength: string;
};

/** Replaces `{name}` placeholders. Unknown names are left unchanged. */
export function interpolateTemplate(input: {
  template: string;
  values: Record<string, string | number>;
}): string {
  return input.template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = input.values[key];
    return value === undefined ? match : String(value);
  });
}

function pickLocalizedString(
  localizations: Record<string, unknown> | undefined,
  key: string,
  fallback: string
): string {
  const value = localizations?.[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export function resolveFormValidationMessages(
  localizations?: Record<string, unknown>
): FormValidationMessages {
  return {
    required: pickLocalizedString(
      localizations,
      'required_error',
      DEFAULT_FORM_VALIDATION_MESSAGES.required
    ),
    invalidDate: pickLocalizedString(
      localizations,
      'invalid_date_error',
      DEFAULT_FORM_VALIDATION_MESSAGES.invalidDate
    ),
    invalidNumber: pickLocalizedString(
      localizations,
      'invalid_number_error',
      DEFAULT_FORM_VALIDATION_MESSAGES.invalidNumber
    ),
    minLength: pickLocalizedString(
      localizations,
      'min_length_error',
      DEFAULT_FORM_VALIDATION_MESSAGES.minLength
    ),
    maxLength: pickLocalizedString(
      localizations,
      'max_length_error',
      DEFAULT_FORM_VALIDATION_MESSAGES.maxLength
    ),
  };
}

export function getFormValidationMessagesFromConfig(input: {
  screen: Screen | unknown;
  language?: string;
}): FormValidationMessages {
  const { localizations } = getPluginConfig<typeof formScreenManifest>(
    formScreenManifest,
    input.screen,
    false,
    input.language ?? 'unknown'
  );

  return resolveFormValidationMessages(
    localizations as Record<string, unknown>
  );
}
